import type { ApiContinueWatchingItem } from "@lumi/data";
import type { Variants } from "framer-motion";
import { Play } from "lucide-react";
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useAuthenticatedClient } from "../../../auth/AuthContext";
import { EmptyState } from "../../../shared/components";
import { ContinueWatchingCard } from "../../../shared/components/content-cards/ContinueWatchingCard";
import { SkeletonHorizontalTitleCard } from "../../../shared/components/content-cards/SkeletonHorizontalTitleCard";
import { useRailNavigation } from "../../../shared/hooks";

interface ContinueWatchingRailProps {
  readonly transitionTo: (path: string, variant?: Variants) => void;
}

// eslint-disable-next-line max-lines-per-function
export function ContinueWatchingRail({ transitionTo }: ContinueWatchingRailProps): ReactElement | null {
  const [items, setItems] = useState<ApiContinueWatchingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authenticatedClient = useAuthenticatedClient();

  // Stable navigation handler (prevent event handler orphaning during View Transitions)
  const handleNavigateBrowse = useCallback(() => {
    transitionTo("/home/browse");
  }, [transitionTo]);

  const { railRef, handleRailFocus, handleRailBlur, handleRailKeyDown, getCardProps } = useRailNavigation({
    itemCount: items.length,
    onAction: (index) => {
      const item = items[index];
      if (!item) return;
      transitionTo(`/home/watch/${item.title.slug}?assetId=${item.assetId}`);
    },
    onInfo: (index) => {
      const item = items[index];
      if (!item) return;
      transitionTo(`/home/title/${item.title.slug}`);
    },
  });

  useEffect(() => {
    async function loadContinueWatching(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        if (!authenticatedClient) {
          setError("No authenticated client");
          return;
        }
        const response = await authenticatedClient.getContinueWatching();
        setItems(response.items);
        setError(null);
      } catch (err) {
        console.error("Failed to load continue watching:", err);
        setError(err instanceof Error ? err.message : "Failed to load");
        setItems([]);
      } finally {
        setLoading(false);
      }
    }

    void loadContinueWatching();
  }, [authenticatedClient]);

  // Retry loading when page becomes visible again (e.g., after laptop sleep/wake)
  useEffect(() => {
    async function retry(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const response = await authenticatedClient.getContinueWatching();
        setItems(response.items);
        setError(null);
      } catch (err) {
        console.error("Retry failed to load continue watching:", err);
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && error && authenticatedClient) {
        // Retry loading when user returns to the tab after an error
        void retry();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [error, authenticatedClient]);

  // Don't show rail if loading failed
  if (error) {
    return null;
  }

  // Show empty state if no items
  if (!loading && items.length === 0) {
    return (
      <section className="space-y-4">
        <div className="container mx-1 px-8">
          <h2 className="text-2xl font-bold text-white">Continue Watching</h2>
        </div>
        <EmptyState
          icon={<Play className="w-16 h-16" />}
          title="Nothing to resume"
          message="Start watching a title to see your progress here and pick up where you left off."
          action={{
            label: "Browse Content",
            onClick: handleNavigateBrowse,
          }}
        />
      </section>
    );
  }

  // Show skeleton while loading
  if (loading) {
    return (
      <section className="space-y-4">
        <div className="container mx-1 px-8">
          <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded" />
        </div>
        <div className="relative -my-12">
          <div className="flex gap-6 overflow-x-auto py-12 -my-12 px-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
            {Array.from({ length: 3 }, (_, i) => (
              <SkeletonHorizontalTitleCard key={`loading-continue-watching-${i}`} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Prevent rail from receiving focus on click - only allow keyboard focus
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
  };

  return (
    <section className="space-y-4" aria-label="Continue Watching">
      <div className="container mx-1 px-8">
        <h2 className="text-2xl font-bold text-white">Continue Watching</h2>
      </div>
      {/* Roving tabindex pattern: ul[tabIndex={0}] enables keyboard focus on the rail container.
                This is intentional and WCAG-compliant for carousel/rail navigation.
                See: https://www.w3.org/WAI/ARIA/apg/patterns/carousel/ */}
      <ul //NOSONAR
        ref={railRef as React.RefObject<HTMLUListElement>}
        data-rail
        tabIndex={0} //NOSONAR
        aria-label="Continue Watching content rail - use arrow keys to navigate"
        className="relative -my-12 focus:outline-none"
        onFocus={handleRailFocus}
        onBlur={handleRailBlur}
        onKeyDown={handleRailKeyDown}
        onPointerDown={handlePointerDown}
      >
        <div className="flex gap-6 overflow-x-auto py-12 -my-12 px-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
          {items.map((item, index) => {
            const cardProps = getCardProps(index);
            return (
              <li key={item.assetId} data-rail-item ref={cardProps.ref}>
                <ContinueWatchingCard
                  title={item.title}
                  index={index}
                  className={cardProps.className}
                  progress={{
                    assetId: item.assetId,
                    progressPercent: item.progressPercent,
                    positionSec: item.positionSec,
                    durationSec: item.durationSec,
                  }}
                  transitionTo={transitionTo}
                />
              </li>
            );
          })}
        </div>
      </ul>
    </section>
  );
}
